import {
  Component,
  Input,
  ChangeDetectionStrategy,
  SimpleChanges,
  OnChanges,
  ViewChild,
  ChangeDetectorRef,
  AfterViewInit,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy
} from '@angular/core';
import { ReportingGroupType } from 'app/internal/connected-apps/models/general-ledger-config';
import { FiscalYear, FundingReportYear, ReportColors, getFiscalYearStartAndEndDates } from 'app/internal/shared';
import { dateRange } from 'app/internal/shared/utils/dayjs.utils';
import { Transaction } from 'app/internal/transactions/shared/models';
import { CashFlowOfReservesReportMonth, RealTimeFinancialHealthReport } from '../../../reports/shared/models';
import { Property } from '../../../property/shared/models';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { CurrencyPipe, formatDate } from '@angular/common';
import * as _ from 'underscore';
import { DxChartComponent, DxPieChartComponent } from 'devextreme-angular';
import { exportWidgets } from 'devextreme/viz/export';
import * as FileSaver from 'file-saver-es';
import { DashboardService } from '../../services/dashboard.service';
import { RealTimeFinancialHealthReportPdfRequest } from 'app/internal/reports/shared/models/real-time-financial-health-report-pdf-request';
import { ExternalLedgerFund } from 'app/internal/connected-apps/models';
import { IntegrationProperty } from 'app/internal/admin/shared/models/integration-property';
import { Store } from '@ngrx/store';
import { getIsClubSubscription } from 'app/internal/workspace/workspace-store/workspace.selectors';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { FinancialAccount } from 'app/internal/accounts/shared/models/financial-account.model';
import { FinancialAccountFund } from 'app/internal/accounts/shared/enums/accounts.enum';

/** Display shape for reserve/operating account tooltips (bankDate, description, balance). */
export interface AccountBalanceDisplay {
  balanceDate: Date;
  description: string;
  balance: number;
}

dayjs.extend(isSameOrAfter);

@Component({
  selector: 'sp-dashboard-current-integration-display',
  templateUrl: './dashboard-current-integration-display.component.html',
  styleUrls: ['./dashboard-current-integration-display.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DashboardCurrentIntegrationDisplayComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {

  @Input() reportData: RealTimeFinancialHealthReport;
  @Input() fiscalYear: FiscalYear;
  @Input() transactions: Transaction[] = [];
  @Input() financialAccounts: FinancialAccount[] = [];
  @Input() property: Property;
  @Input() externalLedgerFunds: ExternalLedgerFund[];
  @Input() integrationProperty: IntegrationProperty;

  @Output() generatePdfStart: EventEmitter<void> = new EventEmitter<void>();
  @Output() generatePdfFinish: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('reserveExpensesBarChart', { static: false }) reserveExpensesBarChart: DxChartComponent;
  @ViewChild('reserveContributionsBarChart', { static: false }) reserveContributionsBarChart: DxChartComponent;
  @ViewChild('projectedExpensesByCategoryPieChart', { static: false }) projectedExpensesByCategoryPieChart: DxPieChartComponent;
  @ViewChild('expendituresByCategoryBarChart', { static: false }) expendituresByCategoryBarChart: DxChartComponent;
  @ViewChild('reserveSummaryBarChart', { static: false }) reserveSummaryBarChart: DxChartComponent;
  @ViewChild('reserveBalanceLineChart', { static: false }) reserveBalanceLineChart: DxChartComponent;
  @ViewChild('fiveYearProjectionComboChart', { static: false }) fiveYearProjectionComboChart: DxChartComponent;

  hasNoComponents = true;
  reportingGroupTypes = ReportingGroupType;
  totalProjectedExpensesByCategory: number;

  fiscalYearStartDate: Date;
  fiscalYearEndDate: Date;
  currentDate = new Date();
  fundTypesForExpense: string[] = [];
  fundTypesForIncome: string[] = [];

  private currentFiscalYearTxns: Transaction[] = [];
  private cashFlowOfReservesLastTwelveMonths: CashFlowOfReservesReportMonth[] = [];
  private tenYearFundingReportSlice: FundingReportYear[] = [];
  private numberOfYearsForProjectedExpensesByCat: number = 1;

  // Key Insights Data (reserve/operating split from financialAccounts)
  reserveAccountBalances: AccountBalanceDisplay[] = [];
  operatingAccountBalances: AccountBalanceDisplay[] = [];
  otherAccountBalances: AccountBalanceDisplay[] = [];

  // DevExtreme Charts
  dxBarChartRedPalette = ['#efbaab', '#e75e39'];
  dxBarChartGreenPalette = ['#cbe4a7', '#6db30b', '#679b1f', '#b7e34f'];
  dxPieChartPalette = ReportColors;

  dxReserveExpensesChartData: any = {
    data: [],
    hasNoData: true
  };
  dxReserveExpensesTooltipProjectedData: any[] = [];
  dxReserveExpensesTooltipActualData: any[] = [];

  dxReserveContributionsChartData: any = {
    data: [],
    hasNoData: true
  };
  dxReserveContributionsTooltipBudgetData: any[] = [];

  dxExpenditureByCategoryChartData: any = {
    data: [],
    showAutoBreak: false
  };
  dxExpenditureByCategoryChartAnnotations: any[] = [];

  dxReserveSummaryChartData: any = {
    data: [],
    hasNoData: true
  };
  dxReserveSummaryTooltipContributionsData: any[] = [];
  dxReserveSummaryTooltipExpensesData: any[] = [];

  dxReserveBalanceChartData: any = {
    data: [],
    hasNoData: true
  };

  dxFiveYearProjectionChartData: any[] = [];

  dxProjectedExpensesByCategoryChart: any = {
    data: [],
    hasNoData: true
  };

  totalDxChartsToExport: number = 0;
  exportedDxChartsCount: number = 0;

  totalReserveIncome;

  allDxCharts: any[] = [];
  originalDxChartSizes: any[] = [];

  /** Club terminology: "Capital Assessment" for Club, "Special Assessment" for HOA */
  specialAssessmentLabel = 'Special Assessment';

  private destroy$ = new Subject<void>();

  constructor(
    private currencyPipe: CurrencyPipe,
    private dashboardService: DashboardService,
    private ref: ChangeDetectorRef,
    private store: Store
  ) { }

  ngOnInit(): void {
    this.store.select(getIsClubSubscription).pipe(takeUntil(this.destroy$)).subscribe((isClub) => {
      this.specialAssessmentLabel = isClub ? 'Capital Assessment' : 'Special Assessment';
      this.ref.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {

    // Note:  The "name" for each chart below must match what is being used in the Server code!  If you change it here,
    // it must be changed in the server C# code as well.
    this.allDxCharts = [
      { name: 'reserveExpensesBarChart', dxChart: this.reserveExpensesBarChart, pdfHeight: 200, pdfWidth: 600 },
      { name: 'reserveContributionsBarChart', dxChart: this.reserveContributionsBarChart, pdfHeight: 200, pdfWidth: 600 },
      { name: 'projectedExpensesByCategoryPieChart', dxChart: this.projectedExpensesByCategoryPieChart, pdfHeight: 500, pdfWidth: 600 },
      { name: 'expendituresByCategoryBarChart', dxChart: this.expendituresByCategoryBarChart, pdfHeight: 400, pdfWidth: 1500 },
      { name: 'reserveSummaryBarChart', dxChart: this.reserveSummaryBarChart, pdfHeight: 400, pdfWidth: 1500 },
      //{ name: 'reserveBalanceLineChart', dxChart: this.reserveBalanceLineChart, pdfHeight: 400, pdfWidth: 600 },
      //{ name: 'fiveYearProjectionComboChart', dxChart: this.fiveYearProjectionComboChart, pdfHeight: 400, pdfWidth: 600 },
    ];

    // Some chart ViewChild 'instances' may not even be rendered in the DOM if they dont have data.  Filter out those undefined charts.
    this.allDxCharts = this.allDxCharts.filter(c => c.dxChart);

    this.totalDxChartsToExport = this.allDxCharts.length;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  ngOnChanges(changes: SimpleChanges): void {

    // Fwiw, this will fire 2-3 times.  Not noticing any performance issues now, but we may need to
    // revisit i.e. possibly make this component a 'smart' component where it injects the Store and
    // internally handles its data observables, rather than using the @Input stuff.

    this.buildSharedChartData(); // This needs to be called first.

    this.buildFundTypes();
    this.buildReserveExpensesChart();
    this.buildReserveContributionsChart();
    this.buildProjectedExpensesByCategoryChart(1); //Default: Show for current year
    this.buildExpendituresByCategoryChart();
    this.buildSummaryOfReservesChart();
    this.buildReserveBalancesChart();
    this.buildFutureProjectionChart();
    this.calculateTotalReserveIncome();
    this.buildAccountBalancesForTooltip();

    if (this.reportData?.componentInventoryReport?.categories?.length > 0) {
      this.hasNoComponents = false;
    }
  }

  buildAccountBalancesForTooltip() {
    const linkedAccounts = this.financialAccounts ?? [];
    const reserve: AccountBalanceDisplay[] = [];
    const operating: AccountBalanceDisplay[] = [];
    const other: AccountBalanceDisplay[] = [];

    if (linkedAccounts.length > 0) {
      for (const fa of linkedAccounts) {
        const item = this.toAccountBalanceDisplay(fa);
        if (item) {
          if (fa.financialAccountFundId === FinancialAccountFund.Reserve) {
            reserve.push(item);
          } else if (fa.financialAccountFundId === FinancialAccountFund.Operating) {
            operating.push(item);
          } else {
            other.push(item);
          }
        }
      }
      this.reserveAccountBalances = reserve;
      this.operatingAccountBalances = operating;
      this.otherAccountBalances = other;
    } 
  }

  private toAccountBalanceDisplay(fa: FinancialAccount): AccountBalanceDisplay | null {
    const balances = fa.financialAccountBalances ?? [];
    if (balances.length === 0) return null;
    const sorted = [...balances].sort((a, b) => {
      const da = dayjs(a.date);
      const db = dayjs(b.date);
      return da.isValid() && db.isValid() ? db.valueOf() - da.valueOf() : 0;
    });
    const latest = sorted[0];
    const amount = latest?.displayBalance ?? latest?.balanceCurrent ?? 0;
    const dateStr = latest?.date;
    const balanceDate = dateStr ? dayjs(dateStr).toDate() : new Date(0);
    const description = fa.name || fa.institutionName || fa.description || 'Account';
    return { balanceDate, description, balance: amount };
  }

  calculateTotalReserveIncome() {
    this.totalReserveIncome = this.reportData.reserveIncomeYtd?.reduce((total, item) => total + item.value, 0);
  }

  hideLegendLabelIfZero(items) {
    items.forEach(item => {
      if (item.series.pointsByArgument['Actual YTD'] && item.series.pointsByArgument['Actual YTD'][0].originalValue == 0) {
        item.visible = false;
      }
    });
    return items;
  }

  customizeDxPieChartLabel(arg) {
    return `${arg.argumentText} ${arg.percentText}`;
  }

  onDxPopoverHiding(e) {
    const isMouseInsidePopover = e.component.content().parentNode.matches(':hover');
    e.cancel = isMouseInsidePopover;
  }

  onDxPopoverContentReady(e) {
    (e.component.content() as HTMLElement).onmouseleave = function () {
      e.component.hide();
    };
  }

  refreshProjectedExpensesByCategoryChart(years) {
    this.numberOfYearsForProjectedExpensesByCat = +years;
    this.buildProjectedExpensesByCategoryChart(+years);
  }

  checkForUnexpectedExpense(seriesName: string, data: any) {

    // if the item is not "actual" data, do not show annotation
    if (seriesName !== 'Actual YTD') return false;

    // If there is a value for both the actual and projected expenses, do not show annotation
    if (data.actual > 0 && data.projection > 0) return false;

    // See if there is another item with a matching name that has a projected expense.
    // Note: Even if the categories are not grouped together, the chart will group categories with the same name
    const match = this.dxExpenditureByCategoryChartData.data.find(x => x.categoryName === data.categoryName);

    if (match && match.projection > 0) return false;

    return true;
  }

  viewPdfClicked() {

    this.generatePdfStart.emit();

    if (this.totalDxChartsToExport > 0) {

      // Resets the width/height of each DXChart so they look better in the PDF
      this.sizeChartsForPdf();

      // Allows the resizing to render and complete before moving onto the Export...
      setTimeout(() => {

        const formData = new FormData();

        this.allDxCharts.forEach((chart) => {

          // Call the DevExtreme "exportWidget" function to create the Blob of the SVG for each DxChart
          exportWidgets([
            [chart.dxChart.instance]
          ], {
            fileName: chart.name,
            format: 'PNG',
            margin: 10,
            onFileSaving: (e) => {

              // Prevents normal export process from continuing...
              e.cancel = true;

              // Note: The "name" field below must match the same parameter name in the API endpoint (See RealTimeFinancialHealthController.cs)
              formData.append('dxChartImageFiles', e.data, chart.name); // "e.data" is a Blob

              // We need to keep track of current number of DXChart widgets exported vs Total, so we dont
              // make the server request before all Charts have been exported.
              this.exportedDxChartsCount += 1;

              this.sendGeneratePdfRequest(formData);
            }
          });
        });
      }, 50);
    } else {
      // Allows the PDF generated even when there are no charts at all (edge case)
      const formData = new FormData();
      this.sendGeneratePdfRequest(formData);
    }
  }

  get isInOlderFiscalYear() {
    return dayjs(this.fiscalYearEndDate).isBefore(this.currentDate, 'date');
  }

  get projectedExpensesPieChartSubtitle() {
    const totalCost =
      this.currencyPipe.transform(this.totalProjectedExpensesByCategory, 'USD', 'symbol-narrow', '1.0-0');

    let label = `Total cost for ${this.fiscalYear?.year}: ${totalCost}`;
    if (this.numberOfYearsForProjectedExpensesByCat != 1) {
      label = `Total cost over next ${this.numberOfYearsForProjectedExpensesByCat} years: ${totalCost}`;
    }

    return label;
  }

  get projectedExpensesPieChartNoDataMessage() {
    let label = `There are no projected expenses for ${this.fiscalYear?.year}.`;
    if (this.numberOfYearsForProjectedExpensesByCat != 1) {
      label = `There are no projected expenses over the next ${this.numberOfYearsForProjectedExpensesByCat} years.`;
    }

    return label;
  }

  onPopoverHiding(e) {
    const isMouseInsidePopover = e.component
      .content()
      .parentNode.matches(':hover');
    e.cancel = isMouseInsidePopover;
    if (!e.component.content().parentNode.getAttribute('mouselistener')) {
      e.component.content().parentNode.addEventListener('mouseleave', () => {
        e.component.hide();
      });
      e.component.content().parentNode.setAttribute('mouselistener', true);
    }
  }

  private buildSharedChartData() {

    // Fiscal Year info
    const fyDateTuple = getFiscalYearStartAndEndDates(this.fiscalYear);
    this.fiscalYearStartDate = fyDateTuple[0];
    this.fiscalYearEndDate = fyDateTuple[1];

    this.tenYearFundingReportSlice =
      this.reportData?.fundingReport?.years?.slice(0, 10); // 10 years    

    // Get the current Fiscal Year Transactions
    this.currentFiscalYearTxns =
      this.transactions.filter(txn => dayjs(txn.transactionDate).isSameOrAfter(this.fiscalYearStartDate) && dayjs(txn.transactionDate).isSameOrBefore(this.fiscalYearEndDate));

    // Cash Flow Reserves
    this.cashFlowOfReservesLastTwelveMonths = this.reportData?.cashFlowOfReserves?.months?.slice(0, 12);
  }

  private buildReserveExpensesChart() {

    const currentFiscalYearFundingReport =
      this.tenYearFundingReportSlice.filter(a => a.year === this.fiscalYear.year);

    const flattenedComponentsAndCategories =
      currentFiscalYearFundingReport.map(fundingReportYear => {
        return fundingReportYear.anticipatedExpendituresComponents.map(component => {
          return {
            anticipatedCost: component.anticipatedExpenditures,
            componentName: component.propertyComponentName,
          };
        });
      }).flat();

    const totalReserveExpenseProjection = flattenedComponentsAndCategories.reduce((a, b) => a + b.anticipatedCost, 0);

    this.dxReserveExpensesChartData.data = [
      { name: 'Projection', value: +totalReserveExpenseProjection.toFixed(2) },
      { name: 'Actual YTD', value: this.reportData.spendingActualYtd }
    ];

    this.dxReserveExpensesChartData.hasNoData =
      this.reportData?.header?.missingGlCodesConfig && flattenedComponentsAndCategories.length === 0 && this.reportData.spendingActualYtd === 0;

    // Build tooltip for Reserve Expenses Chart "Projection" bar    
    const currentFiscalYearsExpenses =
      flattenedComponentsAndCategories
        .sort((a, b) => b.anticipatedCost - a.anticipatedCost)
        .map(x => ({ name: x.componentName, currentCost: x.anticipatedCost }));

    this.dxReserveExpensesTooltipProjectedData = currentFiscalYearsExpenses.slice(0, 5);

    // Build tooltip for Reserve Expenses Chart "Actual YTD" bar
    const reserveExpenseTxns =
      this.currentFiscalYearTxns
        .filter((txn: Transaction) =>
          (txn.reportingGroup === ReportingGroupType.ReserveExpense)
          && (this.fundTypesForExpense.length == 0 || this.fundTypesForExpense.indexOf(txn.fundDescription) >= 0))
        .map((txn: Transaction) => ({
          transactionDate: txn.transactionDate,
          transactionDescription: txn.transactionDescription,
          glCode: txn.ledgerCode,
          amount: txn.creditAmount - Math.abs(txn.debitAmount)
        }))
        .sort((a, b) => a.amount - b.amount);

    this.dxReserveExpensesTooltipActualData = reserveExpenseTxns.slice(0, 5);
  }

  private buildReserveContributionsChart() {

    const reserveIncomeYtdChartData = this.reportData.reserveIncomeYtd.reduce((result, item) => {
      result[item.reportingGroupId] = item.value;
      return result;
    }, { name: 'Actual YTD' });

    const budgetedContributionsYtdChartData = { 0: this.reportData.budgetedContributionsYtd, name: 'Budget YTD' };

    this.dxReserveContributionsChartData.data = [
      budgetedContributionsYtdChartData,
      reserveIncomeYtdChartData
    ];

    this.dxReserveContributionsChartData.hasNoData =
      this.reportData?.header?.missingGlCodesConfig && !this.reportData.budgetedContributionsYtd && !this.reportData.reserveIncomeYtd;

    //Build tooltip for Reserve Contributions YTD "Budget YTD" bar      
    const monthlyReserveContribution = this.reportData?.fundingReport?.yearOneAnnualReserveContribution / 12;
    const nextMonth = dayjs().add(1, 'month').startOf('month').toDate();
    const endDate = this.fiscalYearEndDate < nextMonth ? dayjs(this.fiscalYearEndDate).add(1, 'day').toDate() : nextMonth;
    const datesBetween = dateRange(this.fiscalYearStartDate, endDate, 'month');
    this.dxReserveContributionsTooltipBudgetData =
      datesBetween.map(date => ({
        date: date,
        reserveContribution: monthlyReserveContribution
      }));
  }

  private buildExpendituresByCategoryChart() {

    // "Reserve Study Projection" Bar Tooltip data    
    const currentFiscalYearFundingReport =
      this.tenYearFundingReportSlice.filter(a => a.year === this.fiscalYear.year);

    const flattenedAnticipatedExpenditureComponents =
      currentFiscalYearFundingReport.map(fundingReportYear => {
        return fundingReportYear.anticipatedExpendituresComponents.map(component => {
          return {
            anticipatedCost: component.anticipatedExpenditures,
            componentName: component.propertyComponentName,
            categoryName: component.propertyComponentCategoryName,
            categoryId: component.propertyComponentCategoryId
          };
        });
      }).flat();

    const anticipatedExpenditureComponentsByCategoryIdGrouping =
      _.groupBy(
        _.sortBy(flattenedAnticipatedExpenditureComponents, 'anticipatedCost').reverse(), //Descending order...
        'categoryId');

    // "Actual YTD" Bar Tooltip data
    const reserveExpensesTxns =
      this.currentFiscalYearTxns
        .filter((txn: Transaction) =>
          (txn.reportingGroup === ReportingGroupType.ReserveExpense)
          && (this.fundTypesForExpense.length == 0 || this.fundTypesForExpense.indexOf(txn.fundDescription) >= 0))
        .map((txn: Transaction) => ({
          transactionDate: txn.transactionDate,
          transactionDescription: txn.transactionDescription,
          amount: txn.creditAmount - Math.abs(txn.debitAmount),
          ledgerCode: txn.ledgerCode
        }));

    const expensesByLedgerCodeGroupingArray = _.map(_.groupBy(reserveExpensesTxns, 'ledgerCode'), function (group) {
      return {
        glCode: group[0].ledgerCode,
        transactions: group,
      };
    });

    this.dxExpenditureByCategoryChartData.data =
      this.reportData?.expendituresByCategory?.categories.map(item =>
        Object.create({
          categoryName: item.name,
          categoryId: item.categoryId,
          projection: item.reserveStudyProjection,
          actual: Math.abs(item.actualYearToDate),
          glCodes: item.glCodes,
          actualYtdTooltipData: expensesByLedgerCodeGroupingArray
            .filter(a => item.glCodes?.includes(a.glCode))
            .map((groupedItem) => {
              return groupedItem.transactions;
            })
            .flat()
            .sort((a, b) => a.amount - b.amount)?.slice(0, 5),
          reserveStudyProjectionTooltipData: anticipatedExpenditureComponentsByCategoryIdGrouping[item.categoryId]?.slice(0, 5)
        })
      );

    // This adds a "Scale Break" if the chart range is very large.  It does some calculations and can optionally turn the
    // scale break "on" if needed.
    if (this.dxExpenditureByCategoryChartData?.data?.length > 1) {
      const sorted = this.dxExpenditureByCategoryChartData.data.slice().sort((a, b) => b.projection - a.projection);

      // See the difference between the top 2 Projection values
      const percDiff = 100 * Math.abs((sorted[0].projection - sorted[1].projection) / ((sorted[0].projection + sorted[1].projection) / 2));

      // If greater than 100%, show the Scale Break in the DxChart
      if (percDiff > 100) {
        this.dxExpenditureByCategoryChartData.showAutoBreak = true;
      }
    }

    // Build the array to be used by DevExtreme to show annotations, if applicable
    this.dxExpenditureByCategoryChartData.data.forEach(data => {
      if (this.checkForUnexpectedExpense('Actual YTD', data)) {
        this.dxExpenditureByCategoryChartAnnotations.push({ categoryName: data.categoryName });
      }
    });
  }

  private buildProjectedExpensesByCategoryChart(numberOfYears: number) {

    // Filter by Year Dropdown
    const toYear = this.fiscalYear.year + numberOfYears;
    const filteredFundingReportYears = this.tenYearFundingReportSlice.filter(a => a.year < toYear);

    // Flatten... 
    const flattenedComponentsAndCategories =
      filteredFundingReportYears.map(fundingReportYear => {
        return fundingReportYear.anticipatedExpendituresComponents.map(component => {
          return {
            anticipatedCost: component.anticipatedExpenditures,
            componentName: component.propertyComponentName,
            categoryName: component.propertyComponentCategoryName
          };
        });
      }).flat();

    // Calculate total sum    
    this.totalProjectedExpensesByCategory = flattenedComponentsAndCategories.reduce((a, b) => a + b.anticipatedCost, 0);

    // Re-Group by Category and Sum...
    const categoryGroupWithTotalCost = [];
    flattenedComponentsAndCategories.reduce(
      function (res, value) {
        if (!res[value.categoryName]) {
          res[value.categoryName] = { categoryName: value.categoryName, anticipatedCost: 0 };
          categoryGroupWithTotalCost.push(res[value.categoryName]);
        }
        res[value.categoryName].anticipatedCost += value.anticipatedCost;
        return res;
      }, {});

    this.dxProjectedExpensesByCategoryChart.data = categoryGroupWithTotalCost;

    this.dxProjectedExpensesByCategoryChart.hasNoData = categoryGroupWithTotalCost.length === 0;
  }

  private buildSummaryOfReservesChart() {

    // Builds the data used in the Tooltips
    const startMonth = this.cashFlowOfReservesLastTwelveMonths[0].date; // First month in the 12-month series...

    const mapTransaction = (txn: Transaction) => {
      return {
        transactionDate: txn.transactionDate,
        transactionDateGroup: formatDate(txn.transactionDate, 'MMM yyyy', 'en'),
        transactionDescription: txn.transactionDescription,
        glCode: txn.ledgerCode,
        amount: txn.creditAmount - Math.abs(txn.debitAmount),
        reportingGroup: txn.reportingGroup
      };
    };

    const lastTwelveMonthsOfTransactions =
      this.transactions
        .filter((txn: Transaction) => dayjs(txn.transactionDate).isSameOrAfter(startMonth))
        .sort((a, b) => { return a.transactionDate < b.transactionDate ? 1 : -1; });  //sort desc by date

    // "Reserve Income" Bar Tooltip
    const reserveIncomeTxns =
      lastTwelveMonthsOfTransactions
        .filter((txn: Transaction) =>
          (txn.reportingGroup === ReportingGroupType.ReserveIncomeContribution ||
            txn.reportingGroup === ReportingGroupType.ReserveIncomeInterest ||
            txn.reportingGroup === ReportingGroupType.ReserveIncomeSpecialAssessment ||
            txn.reportingGroup === ReportingGroupType.ReserveIncomeOther)
          && (this.fundTypesForExpense.length == 0 || this.fundTypesForIncome.indexOf(txn.fundDescription) >= 0))
        .map(mapTransaction);
    const incomeByMonthGrouping = _.groupBy(reserveIncomeTxns, 'transactionDateGroup');

    // "Reserve Expenses" Bar Tooltip
    const reserveExpensesTxns =
      lastTwelveMonthsOfTransactions
        .filter((txn: Transaction) =>
          (txn.reportingGroup === ReportingGroupType.ReserveExpense)
          && (this.fundTypesForExpense.length == 0 || this.fundTypesForExpense.indexOf(txn.fundDescription) >= 0))
        .map(mapTransaction);
    const expensesByMonthGrouping = _.groupBy(reserveExpensesTxns, 'transactionDateGroup');

    // "Summary of Reserves - Last 12 Months" chart
    this.dxReserveSummaryChartData.data =
      this.cashFlowOfReservesLastTwelveMonths.map(item => {
        return item.reserveIncome.reduce((result, item2) => {
          result[item2.reportingGroupId] = item2.value;
          return result;
        },
        {
          monthYear: formatDate(item.date, 'MMM yyyy', 'en'), //"Mar 22", "Feb 23", etc...                    
          reserveExpenses: item.reserveExpenses,
          contributionsTooltipData: incomeByMonthGrouping[formatDate(item.date, 'MMM yyyy', 'en')],
          expensesTooltipData: expensesByMonthGrouping[formatDate(item.date, 'MMM yyyy', 'en')]?.slice(0, 5),
          monthlyReserveIncome: item.monthlyReserveIncome
        });
      });

    // For toggling a "No Data" message
    const totalReserveContributions = this.cashFlowOfReservesLastTwelveMonths.reduce((a, b) => a + b.reserveIncome.reduce((c, d) => c + d.value, 0), 0);
    const totalReserveExpenses = this.dxReserveSummaryChartData.data.reduce((a, b) => a + b.reserveExpenses, 0);
    this.dxReserveSummaryChartData.hasNoData = totalReserveContributions === 0 && totalReserveExpenses === 0;
  }

  private buildReserveBalancesChart() {

    // "Reserve Balance - Last 12 Months" chart    
    this.dxReserveBalanceChartData.data =
      this.cashFlowOfReservesLastTwelveMonths.map(item =>
        Object.create({
          monthYear: formatDate(item.date, 'MMM yyyy', 'en'), //"Mar 22", "Feb 23", etc...
          reserveBalance: item.endingReserveBalance ?? 0
        })
      );

    const totalReserveBalance = this.dxReserveBalanceChartData.data.reduce((a, b) => a + b.reserveBalance, 0);
    this.dxReserveBalanceChartData.hasNoData = totalReserveBalance === 0;
  }

  private buildFutureProjectionChart() {

    const fundingReportYears = this.tenYearFundingReportSlice.slice(0, 5); // Slice another 5 years

    this.dxFiveYearProjectionChartData = fundingReportYears.map(x =>
      Object.create({
        year: x.year.toString(),
        totalReserveFunds: x.totalReserveFunds,
        anticipatedExpenditures: x.anticipatedExpenditures,
        endingReserveBalance: x.endingReserveBalance
      })
    );
  }

  private sizeChartsForPdf() {
    this.allDxCharts.forEach(c => {

      //Save off the original sizes so we can re-size later back to the HTML-friendly size
      this.originalDxChartSizes.push({ name: c.name, size: c.dxChart.instance.getSize() });

      //Set the size to the PDF-friendly size and re-render
      c.dxChart.size = { height: c.pdfHeight, width: c.pdfWidth };
      c.dxChart.instance.render();
    });
  }

  private unsizeChartsForPdf() {
    this.allDxCharts.forEach(c => {

      //Find the original HTML-friendly size, set it, and re-render
      const original = this.originalDxChartSizes.find(orig => orig.name === c.name);

      c.dxChart.size = { height: original.size.height, width: original.size.width };
      c.dxChart.instance.render();
    });

    // Forces a refresh...    
    this.ref.detectChanges();
  }

  private sendGeneratePdfRequest(formData) {

    // It's possible the DevExtreme "exportWidget" functionality may take some extra time to process.
    // These counts and checks make sure ALL DxChart SVG's have been exported before we send the request
    // to the server.
    if (this.exportedDxChartsCount == this.totalDxChartsToExport) {

      const request =
        new RealTimeFinancialHealthReportPdfRequest(this.property.propertyId, this.fiscalYear.year, formData);

      this.dashboardService.generateDashboardPdf(request).subscribe((blob) => {

        this.unsizeChartsForPdf();

        this.generatePdfFinish.emit();

        FileSaver.saveAs(blob, blob.filename);
      });

      this.exportedDxChartsCount = 0;
    }
  }

  dxReserveContributionsTooltipActualData(reportingGroupType: number) {
    return this.currentFiscalYearTxns
      .filter((txn: Transaction) => txn.reportingGroup == reportingGroupType
        && (this.fundTypesForIncome.length == 0 || this.fundTypesForExpense.indexOf(txn.fundDescription) >= 0)
      )
      .map((txn: Transaction) => ({
        transactionDescription: txn.transactionDescription,
        transactionDate: txn.transactionDate,
        glCode: txn.ledgerCode,
        amount: txn.creditAmount - Math.abs(txn.debitAmount)
      }))
      .sort((a, b) => { return a.transactionDate < b.transactionDate ? 1 : -1; })  //sort desc by date    
      .slice(0, 12);
  }

  contributionsTooltipActualData(contributionsTooltipData, reportingFroupType) {
    return contributionsTooltipData ? contributionsTooltipData.filter(x => x.reportingGroup == reportingFroupType).slice(0, 5) : [];
  }

  private buildFundTypes() {

    if (this.integrationProperty != null) {
      // Setup income query params
      // See if the integration property has the useReserveFundsForIncome
      if (this.integrationProperty.useReserveFundsForIncome) {
        if (this.externalLedgerFunds != null) {
          for (let i = 0; i < this.externalLedgerFunds.length; i++) {
            // Check if the fund type is set as isReserve
            if (this.externalLedgerFunds[i].isReserve != null && this.externalLedgerFunds[i].isReserve) {
              // If isReserve, add the fund type to the param array
              this.fundTypesForIncome.push(this.externalLedgerFunds[i].fundDescription);
            }
          }
        }
      }

      // Setup expenses query params
      // See if the integration property has the useReserveFundsForExpenses
      if (this.integrationProperty.useReserveFundsForExpenses) {
        if (this.externalLedgerFunds != null) {
          for (let i = 0; i < this.externalLedgerFunds.length; i++) {
            // Check if the fund type is set as isReserve
            if (this.externalLedgerFunds[i].isReserve != null && this.externalLedgerFunds[i].isReserve) {
              // If isReserve, add the fund type to the param array
              this.fundTypesForExpense.push(this.externalLedgerFunds[i].fundDescription);
            }
          }
        }
      }
    }
  }
}
